import type { LayoutListItem } from './api';

// Render wire keys, matching the server (renderMapping.ts): master, the five
// generic environmental slots, and dimensional.
export const RENDER_KINDS = [
  'master',
  'environmental-1',
  'environmental-2',
  'environmental-3',
  'environmental-4',
  'environmental-5',
  'dimensional',
] as const;
export type RenderKind = (typeof RENDER_KINDS)[number];

export const RENDER_LABELS: Record<RenderKind, string> = {
  master: 'Master',
  'environmental-1': 'Environmental 1',
  'environmental-2': 'Environmental 2',
  'environmental-3': 'Environmental 3',
  'environmental-4': 'Environmental 4',
  'environmental-5': 'Environmental 5',
  dimensional: 'Dimensional',
};

// Renders shown as selectable tiles in the detail render grid: the 6 "photos"
// (environmentals + dimensional). The master is the hero/gallery-card image and
// is shown separately, not as a grid tile.
export const GRID_RENDER_KINDS: RenderKind[] = RENDER_KINDS.filter((k) => k !== 'master');

// Renders eligible for a deck: environmental only. Decks are environmental-only
// (the Visualizer's deck generator ignores master/dimensional), so only these
// tiles are deck-selectable in the gallery.
export const DECK_RENDER_KINDS: RenderKind[] = RENDER_KINDS.filter((k) =>
  k.startsWith('environmental'),
);

export interface DeckItem {
  layoutCode: string;          // e.g. "1.03"
  familyName: string;
  familySlug: string;
  totalPriceCents: number;
  itemCount: number;
  widthInches: number | null;
  depthInches: number | null;
  includePricing: boolean;
  selectedRenders: RenderKind[];
  addedAt: number;
}

export interface Deck {
  items: DeckItem[];
  name: string;
  customerName: string;
  updatedAt: number;
}

// v2: render keys changed from the old per-slot model to master/environmental/dimensional.
// Bumping the key abandons stale v1 decks (which held now-invalid selectedRenders keys).
const STORAGE_KEY = 'idea-starter:deck:v2';

export function emptyDeck(): Deck {
  return { items: [], name: '', customerName: '', updatedAt: Date.now() };
}

export function loadDeck(): Deck {
  if (typeof window === 'undefined') return emptyDeck();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyDeck();
    const parsed = JSON.parse(raw) as Deck;
    if (!parsed || !Array.isArray(parsed.items)) return emptyDeck();
    return parsed;
  } catch {
    return emptyDeck();
  }
}

export function saveDeck(deck: Deck): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(deck));
  } catch {
    // quota exceeded or storage disabled — ignore, deck is still in memory
  }
}

export function deckItemFromLayout(layout: LayoutListItem): DeckItem {
  return {
    layoutCode: layout.code,
    familyName: layout.familyName,
    familySlug: layout.familySlug,
    totalPriceCents: layout.totalPriceCents,
    itemCount: layout.itemCount,
    widthInches: layout.widthInches,
    depthInches: layout.depthInches,
    includePricing: true,
    selectedRenders: [...RENDER_KINDS],
    addedAt: Date.now(),
  };
}
